import { C_PERMIT_STATUS_CURRENT } from '../../../common/consts'
import { deriveConfigExpressTaxCertifications } from '../config/configSelectors'
import { setPaymentFees, setPaymentID } from '../payment/paymentSlice'
import {
    taxPaymentPeriodsStarted,
    taxPaymentPeriodFailure,
    taxPaymentPeriodSucceeded,
    calculateTaxStarted,
    calculateTaxFailure,
    calculateTaxSucceeded,
} from './taxSlice'

export const getTaxPaymentPeriodsAsync = (completionCallback = () => null) => {
    return async (dispatch, getState, api) => {
        dispatch(taxPaymentPeriodsStarted())
        try {
            const paymentPeriods = await api.getTaxPaymentPeriods(getState());
            if (paymentPeriods.status === 200) {
                const licenses = await api.getAllLicensesForUserAsync(getState().auth?.idToken);

                // Determine if a license is considered to be inactive
                // A license in inactive if its status is not "current" and it is not actionable
                paymentPeriods?.data?.paymentPeriods.forEach((period) => {
                    period.licenses.forEach((paymentLicense) => {
                        let isInactive = false;
                        if (licenses) {
                            const matchedLicense = licenses.find(
                                (license) => paymentLicense !== null && license.license === paymentLicense.license
                            );

                            if (matchedLicense?.status !== C_PERMIT_STATUS_CURRENT && !paymentLicense.isActionable) {
                                isInactive = true;
                            }
                        }
                        paymentLicense.isInactive = isInactive;
                    });
                });


                dispatch(taxPaymentPeriodSucceeded({ ...paymentPeriods.data, callback: completionCallback }));
            } else {
                dispatch(
                    taxPaymentPeriodFailure(
                        'Something went wrong while fetching tax payment periods.'
                    )
                );
            }
        } catch (error) {
            dispatch(taxPaymentPeriodFailure('Something went wrong while fetching payment periods.'));
        }
    }
}

const transformTaxableReceiptsForCalculateMultiPropertyTot = (paymentPeriods, hasCollectionAgreement, hasCollectionAgreementWith) => {
    const convertedPaymentPeriods = [];

    for (const paymentPeriod of paymentPeriods) {
        const licenses = paymentPeriod.licenses;

        const startDate = paymentPeriod.startDate;
        const endDate = paymentPeriod.endDate;

        for (const license of licenses) {
            const taxableActivities = [];

            const taxableActivity = {
                dateStart: startDate,
                dateEnd: endDate,
                taxableReceipts: parseFloat(license.taxableReceiptsOtherPlatforms ?? 0),
                totalRevenue: parseFloat(license.salesOtherPlatforms ?? 0),
                allowableDeductions: parseFloat(license.deductionsOtherPlatforms ?? 0),
                numDaysOccupied: parseInt(license.daysOccupied),
                numDaysAvailable: parseInt(license.daysAvailable),
                provider: 'other',
            };

            taxableActivities.push(taxableActivity);

            // We only as for the number of days available and number of days occupied once,
            // regardless of the number of providers
            // to avoid the number of days occupied doubling don't pass in the
            // property occupancy fields for providers that are not 'other'
            // DECK-25151
            if (hasCollectionAgreement) {
                const taxableActivityTwo = {
                    dateStart: startDate,
                    dateEnd: endDate,
                    taxableReceipts: parseFloat(license.taxableReceiptsPlatformsThatRemit ?? 0),
                    totalRevenue: parseFloat(license.salesPlatformsThatRemit ?? 0),
                    allowableDeductions: parseFloat(license.deductionsPlatformsThatRemit ?? 0),
                    provider: hasCollectionAgreementWith[0].key, // at this stage just use the first provider as it makes no difference on the back-end calculation
                };

                taxableActivities.push(taxableActivityTwo);
            }

            convertedPaymentPeriods.push({
                license: license.license,
                dateStart: startDate,
                dateEnd: endDate,
                misc: {
                    taxableActivities: taxableActivities,
                },
            });
        }
    }
    return convertedPaymentPeriods
}


const calculateBreakdownValue = (data, key) => {
    let totalDue = 0;
    data.details.forEach(detail => {
        if (detail.misc && detail.misc.taxableActivities) {
            detail.misc.taxableActivities.forEach(activity => {
                if (activity.taxTypes) {
                    Object.values(activity.taxTypes).forEach(taxType => {
                        totalDue += taxType[key] || 0;
                    });
                }
            });
        }
    });
    return totalDue;
};

const calculateBreakdownValueForTaxType = (data, taxTypeKey, key) => {
    let totalDue = 0;
    data.details.forEach(detail => {
        if (detail.misc && detail.misc.taxableActivities) {
            detail.misc.taxableActivities.forEach(activity => {
                if (activity.taxTypes && activity.taxTypes[taxTypeKey]) {
                    totalDue += activity.taxTypes[taxTypeKey][key] || 0;
                }
            });
        }
    });
    return totalDue;
};

const configTaxTypeKeyExistsInData = (data, configKey) => {
    return data.details.some(detail => detail.misc.taxableActivities.some(activity => activity.taxTypes[configKey]));
}

const transformDataForPaymentFees = (data, taxConfig) => {
    // save payment data in redux
    // get total for all breakdown items in the tax config
    let breakdown = [];

    // each tax type is treated individually
    taxConfig.forEach(config => {
        // only include tax type if it is in the config and there is a matching item in the data
        if (configTaxTypeKeyExistsInData(data, config.key)) {
            config.breakdown.forEach(item => {
                if (item.key === "totDue") {
                    const value = calculateBreakdownValueForTaxType(data, config.key, item.key);
                    breakdown.push({ key: `${config.key}_${item.key}`, label: config.label, value: value });
                }
            });
        }
    });

    // fees are summed across tax types
    taxConfig.forEach(config => {
        // only include tax type if it is in the config and there is a matching item in the data
        if (configTaxTypeKeyExistsInData(data, config.key)) {
            config.breakdown.forEach(item => {
                if (!breakdown.some(breakdownItem => breakdownItem.key === item.key)) {
                    if (item.key !== "totDue") {
                        const sum = calculateBreakdownValue(data, item.key);
                        breakdown.push({ key: item.key, label: item.label, value: sum });
                    }
                }
            });
        }
    });

    const paymentFees = {
        ccFee: data?.ccFee,
        ccTotal: data?.ccTotal,
        achFee: data?.achFee,
        achTotal: data?.achTotal,
        achCreditFee: data?.achCreditFee,
        achCreditTotal: data?.achCreditTotal,
        checkFee: data?.checkFee,
        checkTotal: data?.checkTotal,

        breakdown: breakdown
    }

    return paymentFees;
}

const prepareMandatoryAcknowledgements = (taxCertifications = []) =>
    taxCertifications.reduce((acknowledgements, certification, index) => ({
        ...acknowledgements,
        [`aff_${index + 1}`]: { label: certification }
    }), {});

export const calculateMultiPropertyTaxAsync = (paymentPeriods, hasCollectionAgreement, collectionAgreementWith, taxConfig) => {
    return async (dispatch, getState, api) => {
        dispatch(calculateTaxStarted());

        const CHUNK_SIZE = 25;

        const convertedPaymentPeriods =
            transformTaxableReceiptsForCalculateMultiPropertyTot(paymentPeriods, hasCollectionAgreement, collectionAgreementWith);

        const mandatoryAcknowldgements = deriveConfigExpressTaxCertifications(getState());
        const preparedMandatoryAcknowldgement = prepareMandatoryAcknowledgements(mandatoryAcknowldgements);

        try {
            let data;
            let paymentId = null;

            for (
                let chunkStartIdx = 0;
                chunkStartIdx < convertedPaymentPeriods.length;
                chunkStartIdx += CHUNK_SIZE
            ) {
                const chunkEndIdx = Math.min(
                    convertedPaymentPeriods.length,
                    chunkStartIdx + CHUNK_SIZE,
                );

                const paymentPeriodsInChunk =
                    convertedPaymentPeriods.slice(
                        chunkStartIdx,
                        chunkEndIdx,
                    );

                const requestBody = {
                    paymentPeriods: paymentPeriodsInChunk,
                    numPaymentPeriods: convertedPaymentPeriods.length,
                    paymentId,
                    mandatoryAcknowldgements: preparedMandatoryAcknowldgement
                }

                const result = await api.calculateMultiPropertyTotAsync(getState(), requestBody);
                data = result.data;
                paymentId = data.paymentId;
            }

            // save payment data in redux
            const paymentFees = transformDataForPaymentFees(data, taxConfig);

            // save raw response in redux
            dispatch(calculateTaxSucceeded(data));

            // update paymentID in redux
            dispatch(setPaymentID(data.paymentId));

            dispatch(setPaymentFees(paymentFees));

            return true;

        } catch (error) {
            dispatch(calculateTaxFailure('Something went wrong while calculating tax.'));
            return false;
        }
    }
}
